import { lazy, Suspense, useEffect } from 'react'
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute, { AdminRoute } from './components/auth/ProtectedRoute';
import { LoadingSpinner } from './components/ui';
import { useAuthStore, setupTokenRefresh } from './store/authStore';

// 라우트별 코드 스플리팅: 전체 페이지를 한 번에 번들링하지 않고
// 실제로 방문하는 페이지만 그때그때 불러온다 (초기 로딩 용량 절감)
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ComponentDemo = lazy(() => import('./pages/ComponentDemo').then(m => ({ default: m.ComponentDemo })));
const Notices = lazy(() => import('./pages/Notices').then(m => ({ default: m.Notices })));
const SchedulePage = lazy(() => import('./pages/Schedule').then(m => ({ default: m.SchedulePage })));
const UserManagement = lazy(() => import('./pages/UserManagement').then(m => ({ default: m.UserManagement })));
const AIAnalysis = lazy(() => import('./pages/AIAnalysis').then(m => ({ default: m.AIAnalysis })));
const StaffManagement = lazy(() => import('./pages/StaffManagement').then(m => ({ default: m.StaffManagement })));
const IndividualizedServices = lazy(() => import('./pages/IndividualizedServices').then(m => ({ default: m.IndividualizedServices })));
const EmployeeManagement = lazy(() => import('./pages/EmployeeManagement').then(m => ({ default: m.EmployeeManagement })));
const ThemeDemo = lazy(() => import('./pages/ThemeDemo').then(m => ({ default: m.ThemeDemo })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const PurchaseHistory = lazy(() => import('./pages/PurchaseHistory').then(m => ({ default: m.PurchaseHistory })));
const Announcements = lazy(() => import('./pages/Announcements').then(m => ({ default: m.Announcements })));
const WorkScheduleView = lazy(() => import('./pages/WorkScheduleView').then(m => ({ default: m.WorkScheduleView })));
const LeaveManagement = lazy(() => import('./pages/LeaveManagement').then(m => ({ default: m.LeaveManagement })));
const VehicleManagement = lazy(() => import('./pages/VehicleManagement').then(m => ({ default: m.VehicleManagement })));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <LoadingSpinner />
      <p className="mt-4 text-muted-foreground">페이지를 불러오는 중...</p>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    // 앱 최초 로드 시 저장된 인증 상태(토큰)가 실제로 아직 유효한지 서버에 재확인한다.
    // PWA로 설치된 경우(특히 안드로이드)는 저장소가 오래 유지되기 때문에,
    // 만료된 토큰과 "로그인됨" 플래그만 남아 데이터 요청이 계속 401로 실패하는 문제를 막기 위함.
    useAuthStore.getState().checkAuthStatus();
    setupTokenRefresh();

    // 백그라운드에 있던 앱(홈 화면 PWA)이 다시 화면에 나타날 때도 동일하게 재검증한다.
    // 페이지가 완전히 재로딩되지 않고 그대로 복귀하는 경우 마운트 시점 체크만으로는 놓치기 때문.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        useAuthStore.getState().checkAuthStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <ThemeProvider defaultTheme="light" storageKey="intranet-theme">
      <Router>
        <div className="min-h-screen bg-background">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
            {/* 로그인 페이지 */}
            <Route path="/login" element={<Login />} />

            {/* 보호된 라우트들 */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/demo"
              element={<ComponentDemo />}
            />
            <Route
              path="/theme-demo"
              element={<ThemeDemo />}
            />
            <Route
              path="/notices"
              element={
                <ProtectedRoute>
                  <Notices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <ProtectedRoute>
                  <SchedulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-history"
              element={
                <ProtectedRoute>
                  <PurchaseHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-analysis"
              element={
                <AdminRoute pageName="AI 분석" requiredPermission="관리자 권한">
                  <AIAnalysis />
                </AdminRoute>
              }
            />

            {/* 종사자 관련 라우트 */}
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <StaffManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements"
              element={
                <AdminRoute pageName="공지사항" requiredPermission="관리자 권한">
                  <Announcements />
                </AdminRoute>
              }
            />
            <Route
              path="/work-schedule"
              element={
                <ProtectedRoute>
                  <WorkScheduleView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-management"
              element={
                <ProtectedRoute>
                  <LeaveManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vehicles"
              element={
                <ProtectedRoute>
                  <VehicleManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/individualized-services"
              element={
                <ProtectedRoute>
                  <IndividualizedServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees"
              element={
                <AdminRoute pageName="종사자 관리" requiredPermission="관리자 권한">
                  <EmployeeManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <AdminRoute pageName="시스템 설정" requiredPermission="관리자 권한">
                  <Settings />
                </AdminRoute>
              }
            />
            </Routes>
          </Suspense>

          {/* React Hot Toast Container */}
          <Toaster
            position="top-right"
            containerStyle={{ top: 72 }}
            toastOptions={{
              duration: 4000,
              style: {
                background: 'hsl(var(--background))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(8px)',
                opacity: 0.98,
              },
              success: {
                style: {
                  background: 'hsl(var(--background))',
                  color: 'hsl(142, 76%, 36%)',
                  border: '1px solid hsl(142, 76%, 46%)',
                },
              },
              error: {
                style: {
                  background: 'hsl(var(--background))',
                  color: 'hsl(0, 84%, 60%)',
                  border: '1px solid hsl(0, 84%, 60%)',
                },
              },
            }}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App
